// Z-order + duplicate operations for shapes. Render order is governed by
// the explicit `z` field on each shape (see Shape.z). Y.Map iteration is
// stable but its keys can't be reordered after the fact, so z is the only
// reliable handle for "bring to front" / "send to back".
//
// All ops are scoped to a single transaction with a `LOCAL_ORIGIN_*` tag so
// the undo manager collapses them into one step.

import * as Y from 'yjs';
import type { Shape } from '@frame/shared';
import {
  getShapesMap,
  LOCAL_ORIGIN_CREATE,
  LOCAL_ORIGIN_DELETE,
  LOCAL_ORIGIN_EDIT,
  LOCAL_ORIGIN_GROUP,
  LOCAL_ORIGIN_LOCK,
  newShapeId,
} from './binding/shapesMap';

export const DUPLICATE_OFFSET_PX = 20;

function readShape(ymap: Y.Map<unknown>): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  ymap.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
}

function writeShape(target: Y.Map<unknown>, source: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(source)) {
    if (v !== undefined) target.set(k, v);
  }
}

// Find the largest / smallest current z so new / promoted shapes land
// strictly outside the existing range.
function zExtents(shapes: Y.Map<Y.Map<unknown>>): { max: number; min: number } {
  let max = 0;
  let min = 0;
  let seen = false;
  shapes.forEach((ymap) => {
    const z = (ymap.get('z') as number | undefined) ?? 0;
    if (!seen) {
      max = z;
      min = z;
      seen = true;
    } else {
      if (z > max) max = z;
      if (z < min) min = z;
    }
  });
  return { max, min };
}

// Public: next z for a freshly created shape. Callers (makeRect / makeSticky
// etc.) don't have direct access to the doc, so the addShape site reads this
// and stamps it on the shape before insertion.
export function nextZ(doc: Y.Doc): number {
  return zExtents(getShapesMap(doc)).max + 1;
}

// Duplicate selected shapes. Returns the new ids in the same order as input
// so the caller can update selection to the copies.
export function duplicateShapes(
  doc: Y.Doc,
  ids: Iterable<string>,
  authorId: string,
): string[] {
  const shapes = getShapesMap(doc);
  const newIds: string[] = [];
  doc.transact(() => {
    let nextZValue = zExtents(shapes).max + 1;
    for (const id of ids) {
      const src = shapes.get(id);
      if (!src) continue;
      const copy = readShape(src);
      const newId = newShapeId();
      copy.id = newId;
      copy.authorId = authorId;
      copy.createdAt = Date.now();
      copy.z = nextZValue++;
      // Duplicates are intentionally INDEPENDENT — a fresh copy that the
      // user can immediately edit. Carry-through of `locked` (would force
      // an unlock step) or `groupId` (would silently glue the copy to the
      // source's group) both surprise the Miro/Figma mental model.
      delete copy.locked;
      delete copy.groupId;
      if (typeof copy.x === 'number') copy.x = copy.x + DUPLICATE_OFFSET_PX;
      if (typeof copy.y === 'number') copy.y = copy.y + DUPLICATE_OFFSET_PX;
      // Arrow endpoints (point-kind) need the same offset so the copy lives
      // alongside the original instead of dangling at the source coords.
      copy.from = offsetEndpoint(copy.from, DUPLICATE_OFFSET_PX, DUPLICATE_OFFSET_PX);
      copy.to = offsetEndpoint(copy.to, DUPLICATE_OFFSET_PX, DUPLICATE_OFFSET_PX);
      const ymap = new Y.Map<unknown>();
      writeShape(ymap, copy);
      shapes.set(newId, ymap);
      newIds.push(newId);
    }
  }, LOCAL_ORIGIN_CREATE);
  return newIds;
}

function offsetEndpoint(ep: unknown, dx: number, dy: number): unknown {
  if (!ep || typeof ep !== 'object') return ep;
  const e = ep as Record<string, unknown>;
  if (e.kind !== 'point') return ep;
  return {
    ...e,
    x: typeof e.x === 'number' ? e.x + dx : e.x,
    y: typeof e.y === 'number' ? e.y + dy : e.y,
  };
}

// Set each id's z to the next available slot above the current max, in the
// order they appear in the input. Multiple ids preserve relative input order.
export function bringToFront(doc: Y.Doc, ids: Iterable<string>): void {
  const shapes = getShapesMap(doc);
  const list = [...ids].filter((id) => shapes.has(id));
  if (list.length === 0) return;
  doc.transact(() => {
    let next = zExtents(shapes).max + 1;
    for (const id of list) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      ymap.set('z', next++);
    }
  }, LOCAL_ORIGIN_EDIT);
}

// Mirror of bringToFront for the bottom of the stack.
export function sendToBack(doc: Y.Doc, ids: Iterable<string>): void {
  const shapes = getShapesMap(doc);
  const list = [...ids].filter((id) => shapes.has(id));
  if (list.length === 0) return;
  doc.transact(() => {
    let next = zExtents(shapes).min - list.length;
    for (const id of list) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      ymap.set('z', next++);
    }
  }, LOCAL_ORIGIN_EDIT);
}

// Delete helper that matches the undo-origin convention. Re-exported so the
// context menu can call one function instead of importing two modules.
// Locked shapes are skipped so a "delete all" pass can't take them out by
// accident — caller must unlock first.
export function deleteShapesOp(doc: Y.Doc, ids: Iterable<string>): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    for (const id of ids) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      if (ymap.get('locked') === true) continue;
      shapes.delete(id);
    }
  }, LOCAL_ORIGIN_DELETE);
}

// Assign a new shared groupId to every id passed in. Existing groupIds are
// overwritten — grouping a mix of grouped + ungrouped shapes merges them
// into one new group. Returns the assigned groupId (or null when there's
// nothing valid to group). Group of size <2 is a no-op since a single shape
// being "grouped" with itself doesn't change behavior.
export function groupShapes(doc: Y.Doc, ids: Iterable<string>): string | null {
  const shapes = getShapesMap(doc);
  const valid: string[] = [];
  for (const id of ids) {
    if (shapes.has(id)) valid.push(id);
  }
  if (valid.length < 2) return null;
  const groupId = newShapeId('g_');
  doc.transact(() => {
    for (const id of valid) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      ymap.set('groupId', groupId);
    }
  }, LOCAL_ORIGIN_GROUP);
  return groupId;
}

// Strip groupId from every id passed in. Safe on already-ungrouped shapes.
export function ungroupShapes(doc: Y.Doc, ids: Iterable<string>): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    for (const id of ids) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      if (ymap.has('groupId')) ymap.delete('groupId');
    }
  }, LOCAL_ORIGIN_GROUP);
}

// Set the lock flag on each id. `locked=false` deletes the field so the
// shape's serialized form stays clean (omitted-field === unlocked).
export function setLocked(doc: Y.Doc, ids: Iterable<string>, locked: boolean): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    for (const id of ids) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      if (locked) ymap.set('locked', true);
      else if (ymap.has('locked')) ymap.delete('locked');
    }
  }, LOCAL_ORIGIN_LOCK);
}

// Convenience: flip the lock state on a selection. The rule is "if any
// shape in the set is unlocked, lock everything; otherwise unlock". Matches
// Figma/Miro's "Lock selection" toggle behavior.
export function toggleLocked(doc: Y.Doc, ids: Iterable<string>): void {
  const shapes = getShapesMap(doc);
  let anyUnlocked = false;
  for (const id of ids) {
    const ymap = shapes.get(id);
    if (!ymap) continue;
    if (ymap.get('locked') !== true) {
      anyUnlocked = true;
      break;
    }
  }
  setLocked(doc, ids, anyUnlocked);
}

// Read all shapes as plain objects, sorted by z (matches useShapes). Used
// by tests; render code goes through useShapes.
export function listShapes(doc: Y.Doc): Shape[] {
  const shapes = getShapesMap(doc);
  const out: Shape[] = [];
  shapes.forEach((ymap) => {
    const obj: Record<string, unknown> = {};
    ymap.forEach((v, k) => {
      obj[k] = v;
    });
    out.push(obj as unknown as Shape);
  });
  out.sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  return out;
}
